import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {defer, Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {commonFetchListResponse, ListResponse} from '../../common/api-helpers';
import {
    completePendingRequestIfExists,
    createPendingRequest,
    PendingRequest
} from '../../common/request-synchronization';
import {QueryLangFilterOperator} from '../../enums/query-lang-filter-operator';
import {SortDirection} from '../../enums/sort-direction';
import {ListFilterFactory} from '../../factories/list-filter.factory';
import {Asset} from '../../models/asset';
import {CommonListFilter} from '../../models/filters/common-list-filter';
import {QueryLangFilterCriterion} from '../../models/filters/query-lang-filter-criterion';
import {AppConfigService} from '../../services/config.service';
import {AutocompleteProvider, AutocompleteProviderConfig} from '../autocomplete';
import {autocompleteAsset} from './asset-autocomplete';

const ASSET_SYMBOL: keyof Asset = 'symbol';

@Injectable()
export class AssetService implements AutocompleteProvider<Asset> {

    private readonly assetsUrl: string = this.appConfigService.api['assets'];

    private readonly assetsCacheBySymbol: Record<string, Asset> = {};

    private readonly pendingAssetBySymbolReqDict: {[assetSymbol: string]: PendingRequest<Asset>} = {};

    constructor(
        private appConfigService: AppConfigService,
        private httpClient: HttpClient
    ) {}

    public getAssetBySymbolFromCache(symbol: string): Asset {
        return this.assetsCacheBySymbol[symbol];
    }

    public cacheAsset(asset: Asset): void {
        const {symbol} = asset;
        const knownAsset = this.getAssetBySymbolFromCache(symbol);

        if (!knownAsset) {
            this.assetsCacheBySymbol[symbol] = asset;

            completePendingRequestIfExists(
                this.pendingAssetBySymbolReqDict[symbol],
                asset
            );
        }
    }

    public syncAssetWithCache(asset: Asset): Asset {
        const knownAsset = this.getAssetBySymbolFromCache(asset.symbol);

        if (knownAsset) {
            asset = knownAsset;
        } else {
            this.cacheAsset(asset);
        }

        return asset;
    }

    public getAssets(listFilter: CommonListFilter): Observable<ListResponse<Asset>> {
        return commonFetchListResponse(this.httpClient, this.assetsUrl, listFilter);
    }

    public getAssetBySymbol(symbol: string): Observable<Asset> {
        const getPendingRequest = () => {
            const request$ = defer(() =>
                this.createAssetRequestBySymbol(symbol).pipe(
                    tap(asset => {
                        if (asset) {
                            this.cacheAsset(asset);
                        }
                    })
                )
            );

            return this.pendingAssetBySymbolReqDict[symbol]
                || createPendingRequest(
                    request$,
                    newPendingReq => this.pendingAssetBySymbolReqDict[symbol] = newPendingReq,
                    () => delete this.pendingAssetBySymbolReqDict[symbol]
                );
        };

        const knownAsset = this.getAssetBySymbolFromCache(symbol);

        return knownAsset
            ? of(knownAsset)
            : getPendingRequest().request$;
    }

    public autocomplete(
        term: string,
        config?: AutocompleteProviderConfig<Asset>
    ): Observable<Asset[]> {
        return autocompleteAsset(
            listFilter => this.getAssets(listFilter),
            term,
            config
        );
    }

    private createAssetRequestBySymbol(assetSymbol: string): Observable<Asset> {
        const filterBySymbol = new QueryLangFilterCriterion<Asset>({
            fieldNames: [ASSET_SYMBOL],
            operator:   QueryLangFilterOperator.EQ,
            query:      assetSymbol
        });

        const listFilter = ListFilterFactory.createWithSearchFields(
            [filterBySymbol],
            ASSET_SYMBOL,
            SortDirection.ASC,
            1
        );

        return this.getAssets(listFilter).pipe(
            map(listResponse => listResponse.data[0])
        );
    }

}
